import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import CognitiveRadar from "@/components/CognitiveRadar";
import PdfReportMenu from "@/components/PdfReportMenu";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function PatientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/pro/patients/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <p className="p-8">Cargando paciente...</p>;

  const { patient, profile_pre, profile_post, error_types, attempts_count } = data;

  const errorChart = Object.entries(error_types || {}).map(([k, v]) => ({ type: k.replace(/_/g, " "), n: v }));

  const dl = async (path, filename) => {
    const token = localStorage.getItem("sc_access_token");
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  };

  return (
    <div className="space-y-8" data-testid="patient-detail">
      <div className="flex items-center gap-3">
        <Link to="/pro"><Button variant="ghost" size="icon" data-testid="back-to-pro"><ArrowLeft size={18} /></Button></Link>
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-semibold">Paciente</p>
          <h1 className="heading text-3xl font-extrabold mt-1">{patient.name}</h1>
          <p className="text-muted-foreground">{patient.email} · {attempts_count} intentos totales</p>
        </div>
        <div className="flex-1" />
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => dl(`/export/attempts.csv?patient_id=${id}`, `attempts_${patient.email}.csv`)} data-testid="export-csv-btn">
            <FileSpreadsheet size={16} className="mr-1" /> CSV
          </Button>
          <PdfReportMenu patientId={id} patientEmail={patient.email} testIdPrefix="export-pdf" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-border">
          <CardHeader>
            <CardTitle className="heading">Perfil cognitivo</CardTitle>
            <CardDescription>PRE (naranja) vs POST (verde) por dominio.</CardDescription>
          </CardHeader>
          <CardContent>
            {profile_pre
              ? <CognitiveRadar pre={profile_pre} post={profile_post} />
              : <p className="text-muted-foreground">Evaluación PRE pendiente.</p>}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border">
          <CardHeader>
            <CardTitle className="heading">Distribución de errores</CardTitle>
            <CardDescription>Clasificación clínica de errores por tipo.</CardDescription>
          </CardHeader>
          <CardContent>
            {errorChart.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={errorChart} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="type" stroke="hsl(var(--muted-foreground))" width={140} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Bar dataKey="n" fill="hsl(var(--accent))" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted-foreground">Sin errores registrados aún.</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-border">
        <CardHeader>
          <CardTitle className="heading">Últimos intentos</CardTitle>
          <CardDescription>Cronología reciente (máx 50).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Ejercicio</th>
                  <th className="py-2 pr-3">Dominio</th>
                  <th className="py-2 pr-3">Resultado</th>
                  <th className="py-2 pr-3">Tiempo</th>
                  <th className="py-2 pr-3">Tipo error</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_attempts.map((a) => (
                  <tr key={a.id} className="border-b border-border/50">
                    <td className="py-2 pr-3 text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 font-mono">{a.exercise_id}</td>
                    <td className="py-2 pr-3 capitalize">{a.domain.replace(/_/g, " ")}</td>
                    <td className="py-2 pr-3">
                      {a.correct
                        ? <Badge className="bg-primary/15 text-primary">Correcto</Badge>
                        : <Badge className="bg-destructive/15 text-destructive">Error</Badge>}
                    </td>
                    <td className="py-2 pr-3">{a.response_time_ms} ms</td>
                    <td className="py-2 pr-3 text-xs">{a.error_type?.replace(/_/g, " ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
